import { leavePromptSuppressed } from '@/Client/Lib/leavePromptLib';
import { eq } from '@art-suite/art-core-ts';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSharedSettingsSaveQueue } from './settingsSaveQueue';

/** Long enough that typing is not a save per keystroke, short enough to feel automatic. */
const TEXT_SAVE_DELAY_MS = 600;

export type SettingsRecord<Value> = Record<string, Value>;

/**
 * `toStoredForm` for a tab that stores exactly what it sends.
 *
 * Passed explicitly rather than defaulted, so "this tab does not normalize" is a claim someone
 * made rather than one nobody considered (KAIDEV-4345 review round 4).
 */
export const storesWhatItSends = <Value>(record: SettingsRecord<Value>): SettingsRecord<Value> => record;

/**
 * Autosave for an Org Settings tab, keyed by field.
 *
 * The 2026 design carries no save or cancel control - the scope editor persists every change as
 * it is made, and Org Settings follows (KAIDEV-4345). Toggles save the moment they are flipped;
 * text waits for the typing to pause, or for the field to lose focus, whichever comes first.
 *
 * A failed save reverts that one field to what is actually stored and reports why, rather than
 * leaving the page showing an edit the server never accepted - unless the reader has typed again
 * since, in which case their newer text stands. Pending text is flushed on unmount, and reload
 * and close raise the browser's own prompt, so neither can silently discard an edit that looked
 * applied.
 *
 * Generalized from `useModifierAutosave` (KAIDEV-3182), which took two review rounds to harden.
 * Each guard below is one of those findings; none of them is theoretical.
 */
export const useSettingsAutosave = <Value>(
  persisted: SettingsRecord<Value>,
  {
    save: saveRecord,
    validate,
    toStoredForm,
  }: {
    /**
     * Persists the whole record. `update` replaces a prop's value wholesale rather than merging,
     * so every save sends all of it and whatever is sent becomes the truth.
     */
    save: (record: SettingsRecord<Value>) => Promise<unknown>;
    /**
     * Rejects a change before it is sent, returning why. A rejected edit stays on screen so it
     * can be corrected, but never reaches the server - the stored value remains in effect.
     */
    validate?: (key: string, record: SettingsRecord<Value>) => string | undefined;
    /**
     * The form the server will hold for a record this tab sends.
     *
     * Required, and `storesWhatItSends` is how a tab says the two are the same. Three review
     * rounds found three different tabs that normalized without declaring it, each one pinning
     * the guard below for the life of the page, and an optional option is one every new tab can
     * forget in the same way. Opting out is now a decision someone has to write down.
     *
     * Several tabs normalize on the way out and rebuild `persisted` through the same rules, so
     * what comes back never equals what went out: Workflow drops `isEnabled: true` and a label
     * matching the default, Inputs trims. Judging "the settings have caught up" by raw equality
     * therefore never came true on those tabs, and the guard below pinned itself forever
     * (KAIDEV-4345 review rounds 3 and 4). Given the stored form, the comparison is honest.
     */
    toStoredForm: (record: SettingsRecord<Value>) => SettingsRecord<Value>;
  },
) => {
  const [draft, setDraft] = useState<SettingsRecord<Value>>(persisted);
  const [errorByKey, setErrorByKey] = useState<Record<string, string | undefined>>({});

  /* Everything a queued save needs is read through a ref, never closed over. A caller passes a
     fresh `save` closure every render, and depending on it would make the callbacks below
     unstable - which re-runs the flush effect's cleanup on every render instead of on unmount,
     flushing the pending timer, re-rendering, and round again. One keystroke is enough to start
     that loop (#2787 review). */
  const saveRecordRef = useRef(saveRecord);
  saveRecordRef.current = saveRecord;
  const validateRef = useRef(validate);
  validateRef.current = validate;
  const toStoredFormRef = useRef(toStoredForm);
  toStoredFormRef.current = toStoredForm;

  // The draft is what the tab renders and what a save sends, so saves read it from a ref: a
  // debounced flush fires long after the closure that scheduled it was created.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const pendingTimerByKey = useRef<Record<string, ReturnType<typeof setTimeout> | undefined>>({});

  /**
   * What the server is believed to hold. A save sends this with one field's change applied, so
   * saving B cannot carry A's half-typed or rejected edit along with it.
   *
   * Advanced on success rather than waiting for the settings to come back, which is only sound
   * because saves run one at a time: see `saveChainRef`.
   */
  const savedRef = useRef(persisted);

  /**
   * What this page last wrote and the server accepted, per key, until `persisted` agrees.
   *
   * The query cache refetches on tab refocus and on coming back online, and sets whatever the GET
   * returns without checking it against a newer write. A GET that started before a save and lands
   * after it therefore re-seeds the saved key with its pre-save value - and because every save
   * merges onto `savedRef` and sends the whole record, the next save of any other key would write
   * that stale value back to the server (KAIDEV-4345 review round 2).
   *
   * Preferring what we wrote keeps the merge base honest, judged on the stored form so it is
   * released as soon as the settings genuinely carry it.
   *
   * Known limit: this covers the window between a save and the echo of that save coming back.
   * Once the echo lands the confirmation is released, so a GET answered before the save committed
   * and arriving after the echo reverts the field on screen and moves the merge base back - and
   * the next save of any key in that record writes the stale value to the server. Closing it
   * needs a recency signal from the query cache, which this hook cannot derive from values alone;
   * that is KAIDEV-4362. Two admins, or two tabs, writing different sub-records at once is
   * outside both: still last-write-wins per sub-record.
   */
  const confirmedByKey = useRef<SettingsRecord<Value>>({});

  /**
   * How many edits a key has had. A save remembers the count it went out with, so a failure can
   * tell "nothing has happened since" from "the reader has moved on", without comparing values.
   */
  const editSeqByKey = useRef<Record<string, number>>({});

  /**
   * Saves run strictly in order, each payload built when its turn comes.
   *
   * Toggles save the instant they are flipped, so flipping two in quick succession is the
   * ordinary case. Built up front, both payloads would come from the same `savedRef` snapshot and
   * the second would erase the first - and `savedRef` would then hold the erasure, so the lost
   * change would reappear as a field silently reverting on the next refresh.
   */
  const localSaveChainRef = useRef<Promise<void>>(Promise.resolve());

  /* A page whose sections write the same record shares one queue, so their writes cannot overlap
     and lose each other's edits. Without a provider this hook is the only writer and keeps its
     own chain. See `settingsSaveQueue`. */
  const saveChainRef = useSharedSettingsSaveQueue() ?? localSaveChainRef;

  /**
   * How many saves are queued or running per key. A key with one outstanding is mid-edit as much
   * as one with a pending timer: a refresh caused by another field's save must not reset it to a
   * value its own save has not landed yet.
   */
  const outstandingByKey = useRef<Record<string, number>>({});

  /**
   * Keys whose draft deliberately differs from what the server holds: a validation rejection the
   * reader still has to correct, or an added entry whose first save failed.
   *
   * Held separately from `errorByKey` because an error does not by itself mean the draft diverges.
   * A failed save that reverts leaves the draft agreeing with the server, and holding it on the
   * strength of the error alone made that key ignore every later refresh for the life of the page
   * - permanently, in the tabs that never surface or clear an error (KAIDEV-4345 review).
   */
  const heldKeysRef = useRef<Set<string>>(new Set());

  /**
   * Keys with focus in them right now.
   *
   * A refresh must not rewrite a field someone is typing in. Where a tab normalizes - trimming,
   * or emptying a label that matches its default - re-seeding mid-word turns "Paint " into
   * "Paint" under the cursor. The stored form still arrives, on blur, where it reads as the field
   * tidying itself rather than as the page fighting the typist.
   */
  const focusedKeysRef = useRef<Set<string>>(new Set());

  /**
   * Keys introduced by `addEntry` whose first save has not yet succeeded.
   *
   * These are the one case where a failed save must leave the draft alone: a new modifier card
   * that vanished on a failed save would take the reader's half-finished work with it. Every
   * other never-saved key - a sparse notification override, say - has a default to fall back to,
   * and leaving it showing a state the server rejected is the worse outcome.
   */
  const addedKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // A confirmation the settings have caught up with is no longer worth preferring.
    Object.entries(confirmedByKey.current).forEach(([key, value]) => {
      if (eq(persisted[key], value)) delete confirmedByKey.current[key];
    });
    /* A confirmation holds the *stored* form, so it is what the settings will say once they have
       caught up - which is why the draft can prefer it too. In round 2 it held the raw value sent
       and preferring it in the draft hid the server's normalization; that is no longer what it
       is. Seeding a settled key from the stale value instead let the next edit of a sibling
       subfield carry it back to the server (KAIDEV-4345 review round 3). */
    const authoritative: SettingsRecord<Value> = { ...persisted, ...confirmedByKey.current };
    savedRef.current = authoritative;
    setDraft(current => {
      // Keep whatever is mid-edit: a key with a pending save, an outstanding one, a held edit or
      // the reader's focus is something they are still working on, and re-seeding discards it.
      // An error alone is not enough - a failed save that reverted leaves the draft agreeing with
      // the server, and holding it on the error would freeze the key out of every later refresh.
      const inFlight = new Set([
        ...Object.entries(pendingTimerByKey.current).filter(([, timer]) => timer).map(([key]) => key),
        ...Object.entries(outstandingByKey.current).filter(([, count]) => count > 0).map(([key]) => key),
        ...heldKeysRef.current,
        ...focusedKeysRef.current,
      ]);
      if (!inFlight.size) return authoritative;
      const next = { ...authoritative };
      inFlight.forEach(key => {
        // `key in current`, not a truthiness test: `false` and `null` are values a reader chose.
        // Outputs stores bare booleans and a nullable logo, so a toggle switched off with a save
        // in flight used to be reset to the stored value and flick back on (KAIDEV-4345 review).
        if (key in current) next[key] = current[key]!;
        else delete next[key];
      });
      return next;
    });
  }, [persisted]);

  const runSave = useCallback(async (key: string) => {
    const draftEntry = draftRef.current[key];
    if (draftEntry === undefined) return;
    const seqAtSend = editSeqByKey.current[key] ?? 0;
    const merged: SettingsRecord<Value> = { ...savedRef.current, [key]: draftEntry };
    /* The record's key order is the order the tab reads in, so the payload follows the draft
       rather than appending each new key to whatever order the stored record happened to have. */
    const orderedKeys = [...new Set([...Object.keys(draftRef.current), ...Object.keys(merged)])]
      .filter(recordKey => recordKey in merged);
    const record = Object.fromEntries(orderedKeys.map(recordKey => [recordKey, merged[recordKey]!]));

    const rejection = validateRef.current?.(key, record);
    if (rejection) {
      // The edit stays on screen to be corrected, so it must survive a refresh.
      heldKeysRef.current.add(key);
      setErrorByKey(current => ({ ...current, [key]: rejection }));
      return;
    }
    try {
      await saveRecordRef.current(record);
      const storedRecord = toStoredFormRef.current(record);
      savedRef.current = storedRecord;
      const storedEntry = storedRecord[key];
      // Normalized away entirely: the server holds no such key, so there is nothing to prefer.
      if (storedEntry === undefined) delete confirmedByKey.current[key];
      else confirmedByKey.current[key] = storedEntry;
      addedKeysRef.current.delete(key);
      heldKeysRef.current.delete(key);
      setErrorByKey(current => ({ ...current, [key]: undefined }));
    } catch (error) {
      /* Revert only the field that failed: the rest of the record is still what the server has.
         What "revert" means depends on what the server holds for it - the stored value, nothing
         at all, or the reader's own unsaved addition - and the three branches below are those
         three cases. None of them is carried along by the next field's save. */
      /* Only the edit this save carried may be reverted. A reader who kept typing after it left
         has a newer value on screen and another save already queued behind this one; putting the
         stored value back would overwrite their text under the cursor and then send it
         (KAIDEV-4345 review round 2). The error still stands - that save really did fail.

         Counted, not compared: a field edited back to the value this save carried - a toggle
         flipped on, off and on again - is still a newer intent than this save, and comparing
         values would revert it (review round 3). */
      if ((editSeqByKey.current[key] ?? 0) !== seqAtSend) {
        setErrorByKey(current => ({
          ...current,
          [key]: error instanceof Error ? error.message : 'That change could not be saved.',
        }));
        return;
      }

      const stored = savedRef.current[key];
      if (stored !== undefined) {
        heldKeysRef.current.delete(key);
        // `draftRef` too: a save queued behind this one reads it, and would send the pre-revert
        // value if only React state had moved (KAIDEV-4345 review round 3).
        draftRef.current = { ...draftRef.current, [key]: stored };
        setDraft(current => ({ ...current, [key]: stored }));
      } else if (addedKeysRef.current.has(key)) {
        // Nothing to revert to, and nowhere else for the reader's work to live: keep it on screen.
        heldKeysRef.current.add(key);
      } else {
        /* Never saved and not something the reader added, so the server has no such key and the
           control has a default to fall back to. Dropping it reverts the control; keeping it
           would leave a switch showing a state the server refused (KAIDEV-4345 review). */
        heldKeysRef.current.delete(key);
        setDraft(current => {
          const next = { ...current };
          delete next[key];
          return next;
        });
        draftRef.current = (() => {
          const next = { ...draftRef.current };
          delete next[key];
          return next;
        })();
      }
      setErrorByKey(current => ({
        ...current,
        [key]: error instanceof Error ? error.message : 'That change could not be saved.',
      }));
    }
    // Nothing here closes over a value that changes between renders, so this stays stable and
    // every callback built on it does too.
  }, []);

  const save = useCallback((key: string) => {
    outstandingByKey.current[key] = (outstandingByKey.current[key] ?? 0) + 1;
    saveChainRef.current = saveChainRef.current
      // `runSave` handles its own failures; this guards the chain against anything it cannot,
      // since a rejected link would strand every save queued behind it.
      .then(() => runSave(key))
      .catch(() => undefined)
      .finally(() => {
        outstandingByKey.current[key] = (outstandingByKey.current[key] ?? 1) - 1;
      });
    return saveChainRef.current;
    // `saveChainRef` is a ref object either way - this hook's own, or the page's - so it is
    // stable, and listing it keeps that a claim the linter checks rather than one it takes.
  }, [runSave, saveChainRef]);

  const clearPending = useCallback((key: string) => {
    const timer = pendingTimerByKey.current[key];
    if (!timer) return false;
    clearTimeout(timer);
    pendingTimerByKey.current[key] = undefined;
    return true;
  }, []);

  /**
   * Replaces one field's value. Toggles pass `immediate`; text leaves it to the debounce.
   *
   * A value built from the current one is passed as a function, which is resolved against the
   * ref rather than against whatever the caller last rendered: two changes to one field in a
   * single tick would otherwise both read the pre-render value and the first would be lost.
   */
  const updateEntry = useCallback(
    (
      key: string,
      value: Value | ((previous: Value | undefined) => Value),
      { immediate = false } = {},
    ) => {
      const resolved = typeof value === 'function'
        ? (value as (previous: Value | undefined) => Value)(draftRef.current[key])
        : value;
      /* Built outside the updater, not inside it: React runs an updater immediately only when
         nothing else is queued, so with a `setErrorByKey` from a previous save still pending the
         ref would not be set before `save` read it, and the previous value would go. */
      const next = { ...draftRef.current, [key]: resolved };
      draftRef.current = next;
      editSeqByKey.current[key] = (editSeqByKey.current[key] ?? 0) + 1;
      setDraft(next);

      clearPending(key);
      if (immediate) {
        void save(key);
        return;
      }
      pendingTimerByKey.current[key] = setTimeout(() => {
        pendingTimerByKey.current[key] = undefined;
        void save(key);
      }, TEXT_SAVE_DELAY_MS);
    },
    [clearPending, save],
  );

  /**
   * Adds a field at the head of the record and saves it at once.
   *
   * `savedRef` is deliberately not seeded here: until the server accepts it, this entry is not
   * something the server holds, and seeding it would make a failed add ride along on the next
   * field's save.
   */
  const addEntry = useCallback((key: string, value: Value) => {
    const next = { [key]: value, ...draftRef.current };
    draftRef.current = next;
    editSeqByKey.current[key] = (editSeqByKey.current[key] ?? 0) + 1;
    setDraft(next);
    addedKeysRef.current.add(key);
    void save(key);
  }, [save]);

  /**
   * Blur commits whatever is pending for that field without waiting out the pause.
   *
   * `transform` is a last change to apply first - trimming, say - returning `undefined` when
   * there is nothing to change. It is applied against the ref rather than against what the
   * caller last rendered, and a change it makes is itself worth a write.
   *
   * Only a real change is worth one. Every save replaces the whole record, so saving on each
   * blur would send a full-record write for every field a reader merely tabbed through.
   */
  /**
   * Marks a field as having focus, so a refresh leaves it alone until it is committed.
   *
   * Wired to the same inputs as `commitEntry`, which clears the mark on blur.
   */
  const focusEntry = useCallback((key: string) => {
    focusedKeysRef.current.add(key);
  }, []);

  const commitEntry = useCallback(
    (key: string, transform?: (previous: Value | undefined) => Value | undefined) => {
      focusedKeysRef.current.delete(key);
      const transformed = transform?.(draftRef.current[key]);
      if (transformed !== undefined) {
        const next = { ...draftRef.current, [key]: transformed };
        draftRef.current = next;
        editSeqByKey.current[key] = (editSeqByKey.current[key] ?? 0) + 1;
        setDraft(next);
      }
      const wasPending = clearPending(key);
      if (wasPending || transformed !== undefined) {
        void save(key);
        return;
      }

      /* Nothing left to send, so this is the moment the stored form of the field can be shown.
         The refresh that carried it was skipped while the field had focus, and skipping it is
         only honest if the normalization arrives when focus leaves - otherwise the field keeps a
         value the server has already tidied (KAIDEV-4345 review round 2).

         Read from `savedRef`, which prefers what this page wrote: raw settings here would undo
         the reader's own saved edit whenever a stale answer had landed while they were typing,
         or when blur fell between a save resolving and its echo rendering. A held key is skipped
         outright - its draft differs on purpose, and re-seeding would discard the very edit the
         reader is being asked to correct (KAIDEV-4345 review round 3). */
      if (outstandingByKey.current[key] || heldKeysRef.current.has(key)) return;
      const stored = savedRef.current[key];
      if (stored === undefined || eq(stored, draftRef.current[key])) return;
      const next = { ...draftRef.current, [key]: stored };
      draftRef.current = next;
      setDraft(next);
    },
    [clearPending, save],
  );

  const saveRef = useRef(save);
  saveRef.current = save;

  const flushPending = useCallback(() => {
    const timers = pendingTimerByKey.current;
    Object.entries(timers).forEach(([key, timer]) => {
      if (!timer) return;
      clearTimeout(timer);
      timers[key] = undefined;
      void saveRef.current(key);
    });
  }, []);

  /**
   * Reload and close, which the unmount flush never sees.
   *
   * Removing the navigation guards took the browser's own prompt with them: the guard provider
   * registered `beforeunload`, so text inside the debounce window used to be protected on reload
   * and tab close, and afterwards it was dropped silently (KAIDEV-4345 review). The flush on
   * `pagehide` is best effort - a request started that late may not finish - so the prompt is
   * what actually protects the edit, and it is raised whenever a save is pending or running.
   */
  useEffect(() => {
    const hasUnsavedWork = () =>
      Object.values(pendingTimerByKey.current).some(timer => timer)
      || Object.values(outstandingByKey.current).some(count => count > 0)
      // A held key is an edit on screen that the server has not got: a card whose first save was
      // rejected, or a value validation refused to send. Losing it to a reload is the same loss.
      || heldKeysRef.current.size > 0;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedWork() || leavePromptSuppressed()) return;
      event.preventDefault();
      // Required by browsers that predate `preventDefault` alone being enough.
      event.returnValue = '';
    };
    const handlePageHide = () => flushPending();

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [flushPending]);

  useEffect(() => {
    const timers = pendingTimerByKey.current;
    // No dependencies: this must run on unmount and only on unmount. `save` is stable now, but
    // depending on it would make that a promise about a callback rather than about this effect.
    return () => {
      // Flush rather than drop: an edit that appeared to apply must not vanish on navigation.
      Object.entries(timers).forEach(([key, timer]) => {
        if (!timer) return;
        clearTimeout(timer);
        // Clear the entry too: a timer id left truthy reads as still pending to anything that
        // runs afterwards, which would flush it a second time.
        timers[key] = undefined;
        void saveRef.current(key);
      });
    };
  }, []);

  return useMemo(
    () => ({ draft, errorByKey, updateEntry, commitEntry, addEntry, focusEntry }),
    [addEntry, commitEntry, draft, errorByKey, focusEntry, updateEntry],
  );
};
